/**
 * Règles de validation — des fonctions pures, testables sans navigateur.
 *
 * POURQUOI DES RÈGLES PLUTÔT QU'UN BOUTON GRISÉ
 * ---------------------------------------------
 * Vingt-sept formulaires validaient ainsi : un bouton conditionné directement par le texte.
 * Le bouton étant
 * désactivé, il n'y avait rien à valider — et l'utilisateur n'apprenait JAMAIS ce qui
 * manquait. Sur un formulaire à quatre champs, il fallait deviner lequel.
 *
 * Une règle rend `null` quand la valeur convient, et sinon LE MESSAGE À AFFICHER. Elle ne
 * décide pas de l'affichage, elle fournit la phrase — c'est ce qui la rend testable seule.
 */
import type { Variables } from '@/i18n/language'

export type Traduire = (cle: string, repli?: string, variables?: Variables) => string

/** `null` si la valeur est acceptable, sinon le message destiné à l'utilisateur. */
export type Regle = (valeur: string, t: Traduire) => string | null

export const requis: Regle = (v, t) =>
  v.trim() ? null : t('champ.requis', 'Ce champ est obligatoire')

export const longueurMin = (n: number): Regle => (v, t) =>
  // Un champ vide relève de `requis` : cumuler les deux messages dirait deux fois la même
  // chose sur un champ qu'on n'a pas encore rempli.
  !v.trim() || v.trim().length >= n ? null
    : t('champ.longueurMin', 'Au moins {{n}} caractères — il en manque {{manque}}',
        { n, manque: n - v.trim().length })

export const longueurMax = (n: number): Regle => (v, t) =>
  v.length <= n ? null
    : t('champ.longueurMax', '{{n}} caractères au maximum ({{actuel}} saisis)',
        { n, actuel: v.length })

export const entierPositif: Regle = (v, t) =>
  !v.trim() || /^\d+$/.test(v.trim()) ? null
    : t('champ.entierPositif', 'Un nombre entier positif est attendu')

/**
 * Règle inter-champs : la valeur ne doit pas précéder celle d'un autre champ.
 *
 * La référence est une FONCTION, lue au moment de la validation. Passer la valeur figerait
 * celle du premier rendu, et la règle mentirait dès que l'autre champ change.
 *
 * Les dates d'un `<input type="date">` sont en `AAAA-MM-JJ` : la comparaison de chaînes est
 * exacte, sans conversion ni fuseau horaire.
 */
export const pasAvant = (reference: () => string, libelleRef: string): Regle => (v, t) => {
  const ref = reference()
  return !v || !ref || v >= ref ? null
    : t('champ.pasAvant', 'Doit être postérieur ou égal à « {{ref}} »', { ref: libelleRef })
}
